import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { History, Calendar, Search } from "lucide-react";

/**
 * Historical Land Intelligence — Phase 1 (strictly additive).
 * Consumes the EXISTING `/api/land/parcels/{id}` endpoint which already
 * returns a `history[]` array from the immutable `parcel_history` collection.
 * No backend endpoint modified. No parcel record mutated.
 */
export const HistoricalTimeline = () => {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  const list = useQuery({
    queryKey: ["hist-list", q],
    queryFn: async () => (await api.get("/land/parcels", { params: { q: q || undefined, limit: 30 } })).data,
  });

  const detail = useQuery({
    queryKey: ["hist-detail", selectedId],
    queryFn: async () => (await api.get(`/land/parcels/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  const events = useMemo(() => {
    const p = detail.data;
    if (!p) return [];
    // Synthesise a timeline entry for parcel creation if history array is empty.
    const items = [...(p.history || [])];
    if (p.created_at) {
      items.push({
        action: "parcel_created", timestamp: p.created_at,
        detail: `Parcel ${p.parcel_number} registered as ${p.land_type} under ${p.tenure}.`,
      });
    }
    if (p.title_registered) {
      items.push({
        action: "title_registered", timestamp: p.created_at,
        detail: `Title issued to ${p.rights_holder}.`,
      });
    }
    return items.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
  }, [detail.data]);

  const years = useMemo(() => {
    const ys = new Set(events.map((e) => (e.timestamp || "").slice(0, 4)).filter(Boolean));
    return Array.from(ys).sort();
  }, [events]);

  const visible = events.filter((e) => !year || (e.timestamp || "").startsWith(String(year)));

  return (
    <div className="space-y-6" data-testid="page-historical-timeline">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Historical Land Intelligence</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <History className="text-[#C99A2E]"/> Parcel Timeline Engine
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-3xl leading-relaxed">
          Immutable playback of every recorded change to a parcel — ownership, tenure, subdivision, boundary and administrative events. Historical records are never mutated.
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <aside className="cad-panel p-4">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5] mb-2">Find a parcel</div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-[#7C93A5]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hist-search"
                   placeholder="Parcel # or district"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm pl-8 pr-2 py-2 text-[13px] text-[#F6F8FA]"/>
          </div>
          <div className="mt-3 space-y-1 max-h-[420px] overflow-y-auto">
            {(list.data?.items || []).map((p) => (
              <button key={p.id} onClick={() => setSelectedId(p.id)} data-testid={`hist-pick-${p.id}`}
                      className={`w-full text-left px-2 py-1.5 rounded-sm text-[12.5px] font-mono-cad
                                  ${selectedId === p.id ? "bg-[#145A7A]/40 text-[#F6F8FA]" : "text-[#DCE3E8] hover:bg-[#145A7A]/20"}`}>
                {p.parcel_number}
                <div className="text-[10px] text-[#7C93A5] uppercase tracking-widest">{p.district}, {p.province}</div>
              </button>
            ))}
          </div>
        </aside>

        <section className="lg:col-span-3 cad-panel p-5">
          {!detail.data && (
            <div className="text-[13px] text-[#B3C2CC]">Pick a parcel from the left panel to explore its full history.</div>
          )}
          {detail.data && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Selected parcel</div>
                  <div className="font-display text-xl font-bold text-[#F6F8FA]">{detail.data.parcel_number}</div>
                  <div className="text-[12px] text-[#B3C2CC]">{detail.data.district}, {detail.data.province} · {detail.data.area_ha} ha · {detail.data.land_type}</div>
                </div>
                <div className="flex items-center gap-2 text-[12px]">
                  <Calendar size={12} className="text-[#C99A2E]"/>
                  <input type="range" min={2000} max={new Date().getFullYear()} step={1}
                         value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                         data-testid="hist-year"
                         className="accent-[#C99A2E]"/>
                  <span className="font-mono-cad text-[#F6F8FA] w-12 text-right">{year}</span>
                </div>
              </div>

              <div className="mt-5">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mb-2">
                  Recorded events {visible.length > 0 ? `(${visible.length} in ${year})` : `(none in ${year})`}
                </div>
                <ol className="relative border-l cad-hairline pl-5 space-y-4">
                  {visible.length === 0 && (
                    <li className="text-[13px] text-[#7C93A5] italic">No recorded events for this year. Drag the slider or select another parcel.</li>
                  )}
                  {visible.map((e, i) => (
                    <li key={i} className="relative" data-testid={`hist-event-${i}`}>
                      <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-[#C99A2E] border-2 border-[#071B2B]"/>
                      <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">{e.timestamp?.slice(0, 19).replace("T", " ")}</div>
                      <div className="font-display font-bold text-[#F6F8FA] mt-0.5 capitalize">{(e.action || e.decision || "event").replace(/_/g, " ")}</div>
                      {e.detail && <div className="text-[13px] text-[#B3C2CC] mt-1">{e.detail}</div>}
                    </li>
                  ))}
                </ol>

                {years.length > 0 && (
                  <div className="mt-5 text-[11px] font-mono-cad uppercase tracking-[0.22em] text-[#7C93A5]">
                    Years with recorded events: {years.join(" · ")}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};
